'use client';

import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Checkbox } from '@common/components';
import { cn } from '@core/utils';
import { useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';

import type { CategoryOption, InstitutionOption } from '../types';

interface SideFilterProps {
  institutionOptions: InstitutionOption[];
  categoryOptions: CategoryOption[];
  institutionId?: number;
  categoryId?: number;
  isStockOnly?: boolean;
  className?: string;
}

export const SideFilter = ({
  institutionOptions,
  categoryOptions,
  institutionId,
  categoryId,
  isStockOnly,
  className,
}: SideFilterProps) => {
  const router = useCustomRouter();
  const [openSections, setOpenSections] = useState({
    institution: true,
    category: true,
  });

  const toggleSection = (section: 'institution' | 'category') => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <section className={cn('flex w-160 flex-col gap-40 pt-36', className)}>
      {/** 카테고리 필터 */}
      <div className="flex flex-col gap-16">
        <button
          type="button"
          onClick={() => toggleSection('category')}
          aria-expanded={openSections.category}
          className="flex w-full items-center justify-between"
        >
          <p className="font-700 text-Gray-900 typo-body2">답례품</p>
          <SvgChevrondown
            className={cn('size-16 text-Gray-black duration-300', {
              'rotate-180': openSections.category,
            })}
          />
        </button>
        <div
          className={cn(
            'grid transition-all duration-300 ease-in-out',
            openSections.category ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <ul className="flex flex-col gap-12 overflow-hidden px-12">
            {categoryOptions.map(({ label, value, newProductYN }) => {
              const isActive =
                categoryId === value ||
                (categoryId === undefined && value === 0);

              return (
                <li key={value} className="flex items-center gap-4">
                  <CustomLink
                    href={DONATION_ROUTES.GIFTS.withSearchParams({
                      searchParams: {
                        isStockOnly,
                        categoryId: value ? value : undefined,
                      },
                    })}
                    aria-label="카테고리 필터 변경"
                    className={cn(
                      'font-500 text-Gray-550 duration-150 typo-body3',
                      {
                        'font-600 text-Gray-900': isActive,
                        'hover:text-Gray-700': !isActive,
                      },
                    )}
                  >
                    {label}
                  </CustomLink>
                  {newProductYN && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle cx="7.99805" cy="8" r="8" fill="#F5511D" />
                      <path
                        d="M10.3684 5V11H9.40711L6.56457 6.89779H6.51485V11H5.4375V5H6.4154L9.24137 9.10221H9.29938V5H10.3684Z"
                        fill="white"
                      />
                    </svg>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      {/** 지자체 필터 */}
      <div className="flex flex-col gap-16">
        <button
          type="button"
          onClick={() => toggleSection('institution')}
          aria-expanded={openSections.institution}
          className="flex w-full items-center justify-between"
        >
          <p className="font-700 text-Gray-900 typo-body2">지자체</p>
          <SvgChevrondown
            className={cn('size-16 text-Gray-black duration-300', {
              'rotate-180': openSections.institution,
            })}
          />
        </button>
        <div
          className={cn(
            'grid transition-all duration-300 ease-in-out',
            openSections.institution ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <ul className="flex flex-col gap-12 overflow-hidden px-12">
            {institutionOptions.map(
              ({
                label,
                //  newDisplayYn,
                value,
              }) => {
                const isActive =
                  institutionId === value ||
                  (institutionId === undefined && value === 0);

                return (
                  <li key={value} className="flex items-center gap-4">
                    <CustomLink
                      href={DONATION_ROUTES.GIFTS.withSearchParams({
                        searchParams: {
                          isStockOnly,
                          institutionId: value ? value : undefined,
                        },
                      })}
                      aria-label="지자체 필터 변경"
                      className={cn(
                        'font-500 text-Gray-550 duration-150 typo-body3',
                        {
                          'font-600 text-Gray-900': isActive,
                          'hover:text-Gray-700': !isActive,
                        },
                      )}
                    >
                      {label}
                    </CustomLink>
                    {/* new 임시 숨김 처리 */}
                    {/* {newDisplayYn && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle cx="7.99805" cy="8" r="8" fill="#F5511D" />
                      <path
                        d="M10.3684 5V11H9.40711L6.56457 6.89779H6.51485V11H5.4375V5H6.4154L9.24137 9.10221H9.29938V5H10.3684Z"
                        fill="white"
                      />
                    </svg>
                  )} */}
                  </li>
                );
              },
            )}
          </ul>
        </div>
      </div>
      {/** 품절상품 제외 여부 필터 */}
      <Checkbox
        checked={isStockOnly}
        onCheckedChange={(checked) => {
          router.replace(
            DONATION_ROUTES.GIFTS.withSearchParams({
              searchParams: {
                institutionId,
                categoryId,
                isStockOnly: checked,
              },
            }),
          );
        }}
      >
        품절상품 제외
      </Checkbox>
    </section>
  );
};
